import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { useBranch } from '@/contexts/BranchContext'
import { productService } from '@/services/productService'

export default function ProductsPage() {
  const { currentBranch } = useBranch()
  const [loading, setLoading] = useState(true)
  const [products, setProducts] = useState([])
  const [search, setSearch] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState({ name: '', category: 'product', price: 0, isActive: true })

  const load = async () => {
    if (!currentBranch?.id) return
    try {
      setLoading(true)
      const list = await productService.listByBranch(currentBranch.id)
      setProducts(list)
    } catch (e) {
      console.error(e)
      toast.error('Failed to load products')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranch?.id])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return products
    return products.filter((p) => (p.name || '').toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q))
  }, [products, search])

  const openCreate = () => {
    setEditing(null)
    setForm({ name: '', category: 'product', price: 0, isActive: true })
    setShowModal(true)
  }

  const openEdit = (p) => {
    setEditing(p)
    setForm({
      name: p.name || '',
      category: p.category || 'product',
      price: Number(p.price || 0),
      isActive: p.isActive !== false,
    })
    setShowModal(true)
  }

  const save = async () => {
    if (!currentBranch?.id) return
    if (!form.name.trim()) {
      toast.error('Name is required')
      return
    }
    try {
      const payload = { ...form, branchId: currentBranch.id }
      if (editing?.id) {
        await productService.update(editing.id, payload)
        toast.success('Product updated')
      } else {
        await productService.create(payload)
        toast.success('Product created')
      }
      setShowModal(false)
      await load()
    } catch (e) {
      console.error(e)
      toast.error('Failed to save product')
    }
  }

  const remove = async (p) => {
    if (!confirm(`Delete product “${p.name}”?`)) return
    try {
      await productService.remove(p.id)
      toast.success('Product deleted')
      await load()
    } catch (e) {
      console.error(e)
      toast.error('Failed to delete product')
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Products</h1>
          <p className="text-sm text-muted-foreground">Products shown in POS come from here.</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Add product
        </button>
      </div>

      <div className="rounded-xl border border-border/80 bg-card p-4 shadow-sm">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products…"
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
        />
      </div>

      <div className="rounded-xl border border-border/80 bg-card shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr className="text-left text-xs font-semibold text-muted-foreground">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td className="px-4 py-4 text-sm text-muted-foreground" colSpan={5}>
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-sm text-muted-foreground" colSpan={5}>
                    No products yet. Click “Add product”.
                  </td>
                </tr>
              ) : (
                filtered.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{p.name}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{p.category || 'product'}</td>
                    <td className="px-4 py-3 text-sm text-foreground">R{Number(p.price || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{p.isActive === false ? 'No' : 'Yes'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => openEdit(p)}
                          className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-muted/40"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </button>
                        <button
                          onClick={() => remove(p)}
                          className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-muted/40"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-card p-5 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground">{editing ? 'Edit product' : 'Add product'}</h2>
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-muted/40"
              >
                Close
              </button>
            </div>
            <div className="grid gap-3">
              <div className="grid gap-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="grid gap-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Category</label>
                <input
                  value={form.category}
                  onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                  placeholder="e.g. beverage, snack, product"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="grid gap-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Price (VAT inclusive)</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={Number(form.price || 0)}
                  onChange={(e) => setForm((p) => ({ ...p, price: Number(e.target.value || 0) }))}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={form.isActive !== false}
                  onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                />
                Active
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-muted/40"
              >
                Cancel
              </button>
              <button
                onClick={save}
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

